import { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SettingsPageLayoutProps {
  /**
   * The title of the settings page (translation key)
   */
  title: string;
  /**
   * The content of the settings page
   */
  children: ReactNode;
  /**
   * Optional custom breadcrumb text (translation key). If not provided, uses title.
   */
  breadcrumbText?: string;
  /**
   * Optional loading state - shows skeleton loader when true
   */
  isLoading?: boolean;
}

/**
 * Shared layout component for all Settings pages.
 * Provides consistent structure with:
 * - Outer wrapper with sd-scope and sd-font
 * - Max width container (max-w-3xl) with padding and spacing
 * - Breadcrumb navigation
 * - Page title
 * - Optional loading state
 */
export const SettingsPageLayout = ({
  title,
  children,
  breadcrumbText,
  isLoading = false,
}: SettingsPageLayoutProps) => {
  const { t } = useTranslation();

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        {/* Breadcrumb */}
        <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground'>
          <span>{t('settings')}</span>
          <ChevronRight className='h-3.5 w-3.5' />
          <span className='text-sd-foreground font-medium'>{t(breadcrumbText || title)}</span>
        </nav>

        {/* Loading State */}
        {isLoading ? (
          <div className='space-y-3'>
            <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8'>
              <div className='animate-pulse space-y-4'>
                <div className='h-4 bg-sd-muted rounded w-3/4'></div>
                <div className='h-4 bg-sd-muted rounded w-1/2'></div>
                <div className='h-4 bg-sd-muted rounded w-5/6'></div>
              </div>
            </div>
          </div>
        ) : (
          /* Page Content */
          children
        )}
      </div>
    </div>
  );
};
